'use client';
import { useCurrentPlayingAudioUrl } from '@mnd-frontend/contexts';
import { Icons, Spinner } from '@mnd-frontend/ui/publish/components';
import { useToasts, useTranslation } from '@mnd-frontend/ui/publish/contexts';
import {
  GREY_700,
  GREY_1000,
  NEUTRAL_WHITE,
  PURPLE_600,
  PURPLE_700,
  RED_600,
  RED_700,
  SIZING_1_5,
  SIZING_2,
  SIZING_4,
  SIZING_6,
  SPACING_1,
  SPACING_2,
  WARNING_100,
} from '@mnd-frontend/ui/shared/tokens';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const AudioPlayerContainer = styled.div<{
  $compact: boolean;
  $visible: boolean;
}>`
  display: ${props => (props.$visible ? 'flex' : 'none')};
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_2)};
`;

const Hitline = styled.span<{ $compact: boolean }>`
  color: ${GREY_1000};
  font-weight: 400;
  font-size: ${props => (props.$compact ? SIZING_1_5 : SIZING_2)};
  line-height: 1.375;
  margin-right: 8px;

  b {
    font-weight: 600;
    background-color: ${WARNING_100};
  }
`;

const PlayButton = styled.button<{ $compact: boolean; $playState: PlayState }>`
  all: unset;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  background-color: ${props =>
    ['playing', 'loading'].includes(props.$playState) ? RED_700 : PURPLE_700};
  color: ${NEUTRAL_WHITE};
  width: ${props => (props.$compact ? SIZING_4 : SIZING_6)};
  height: ${props => (props.$compact ? SIZING_4 : SIZING_6)};
  border-radius: ${props => (props.$compact ? SIZING_4 : SIZING_6)};

  &:hover {
    background-color: ${props =>
      ['playing', 'loading'].includes(props.$playState) ? RED_600 : PURPLE_600};
  }

  svg path {
    fill: ${NEUTRAL_WHITE};
  }
`;

const Timestamp = styled.span<{ $compact: boolean }>`
  display: inline-block;
  color: ${GREY_700};
  font-size: ${props => (props.$compact ? '0.625rem' : '0.875rem')};
`;

const PlayIcon = styled(Icons.PlayFilled)<{ $compact: boolean }>`
  position: relative;
  left: ${props => (props.$compact ? '1px' : '2px')};
`;

const IconsSoundwaves = styled(Icons.Soundwaves)`
  display: inline-block;
  ${PlayButton}:hover & {
    display: none;
  }
`;

const IconsStop = styled(Icons.Stop)`
  display: none;
  ${PlayButton}:hover & {
    display: inline-block;
  }
`;

const TextContainer = styled.div`
  display: inline-flex;
  align-items: center;
`;

const formatTimestamp = (timestamp: number): string => {
  const hours = Math.floor(timestamp / 3600);
  const minutes = Math.floor((timestamp % 3600) / 60);
  const seconds = Math.floor(timestamp % 60);
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return hours > 0
    ? `${hours}:${formattedMinutes}:${formattedSeconds}`
    : `${formattedMinutes}:${formattedSeconds}`;
};

export type AudioHitline = {
  id: string;
  startPos: number;
  endPos: number;
  hitline: string;
};

export type AudioPlayerProps = {
  audioUrl: string;
  audioHitline: AudioHitline;
  compact?: boolean;
  visible?: boolean;
};

type PlayState = 'pristine' | 'playing' | 'stopped' | 'loading';

const AudioPlayer = ({
  audioUrl,
  audioHitline: { startPos, endPos, hitline, id: hitlineId },
  compact = false,
  visible = true,
}: AudioPlayerProps) => {
  const { t } = useTranslation();
  const { showFailureToast } = useToasts();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playState, setPlayState] = useState<PlayState>('pristine');
  const { currentlyPlayingAudioUrl, setCurrentlyPlayingAudioUrl } = useCurrentPlayingAudioUrl();
  const audioUrlWithId = `${audioUrl}#${hitlineId}`;

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playState === 'playing') {
      audio.pause();
      audio.currentTime = startPos;
      setPlayState('stopped');
      return;
    }

    let timeout;
    try {
      timeout = setTimeout(() => setPlayState('loading'), 150);
      audio.currentTime = startPos;
      await audio.play();
      clearTimeout(timeout);
      setPlayState('playing');
      setCurrentlyPlayingAudioUrl(audioUrlWithId);
    } catch {
      clearTimeout(timeout);
      showFailureToast();
      setPlayState('stopped');
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (
      (playState === 'playing' && currentlyPlayingAudioUrl !== audioUrlWithId) ||
      (!visible && playState === 'playing')
    ) {
      audio.pause();
      audio.currentTime = startPos;
      setPlayState('stopped');
    }
  }, [currentlyPlayingAudioUrl, audioUrlWithId, playState, startPos, visible]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      if (audio.currentTime > endPos) {
        audio.pause();
        audio.currentTime = startPos;
        setPlayState('stopped');
      }
    };

    const onCanPlay = () => {
      if (playState === 'loading') {
        setPlayState('playing');
      }
      if (playState === 'playing' && audio.currentTime >= endPos) {
        audio.pause();
        audio.currentTime = startPos;
        setPlayState('stopped');
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('canplay', onCanPlay);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('canplay', onCanPlay);
    };
  }, [startPos, endPos, playState]);

  return (
    <AudioPlayerContainer
      $compact={compact}
      $visible={visible}
      role="region"
      aria-label={t('common_media_control_audio_player')}
    >
      <audio ref={audioRef} src={audioUrl} preload="none">
        <track kind="captions" />
      </audio>
      {playState === 'loading' ? (
        <PlayButton
          type="button"
          aria-disabled="true"
          aria-busy="true"
          aria-label={t('common_media_control_loading')}
          $compact={compact}
          $playState={playState}
        >
          <Spinner size={compact ? 'xs' : 'sm'} />
        </PlayButton>
      ) : (
        <PlayButton
          type="button"
          onClick={togglePlay}
          aria-label={
            playState === 'playing'
              ? t('common_media_control_stop')
              : t('common_media_control_play')
          }
          $compact={compact}
          $playState={playState}
        >
          {playState === 'playing' ? (
            <>
              <IconsSoundwaves $size={compact ? 'xxxs' : 'xxs'} />
              <IconsStop $size={compact ? 'xxxs' : 'xxs'} />
            </>
          ) : (
            <PlayIcon $size={compact ? 'xxxs' : 'xxs'} $compact={compact} />
          )}
        </PlayButton>
      )}
      <TextContainer>
        <div>
          <Hitline $compact={compact} dangerouslySetInnerHTML={{ __html: `"${hitline}"` }} />
          <Timestamp $compact={compact}>
            {formatTimestamp(startPos)} - {formatTimestamp(endPos)}
          </Timestamp>
        </div>
      </TextContainer>
    </AudioPlayerContainer>
  );
};

export default AudioPlayer;
